import React from 'react';
import {
  ListProps,
  TextField,
  FunctionField,
  useRedirect,
} from 'react-admin';

import {
  TextInput,
  Grid,
  GridList,
  Forbidden,
  SelectArrayInput,
  DateField,
  PhoneField,
  MoneyField,
  NullableBooleanInput,
  DateRangeSelect, CheckboxField,
} from '@components';
import { CxCredit, CxData } from '@types';
import { cxHttp } from '@network';
import { exportToCsv, rbac } from '@utils';
import { useExporter, useRbacList } from '@hooks';
import { DonePendingField } from '../DonePendingField';
import { CreditStatusField, cxStatusOptions } from '@pages/credit/CreditStatusField';
import cls from './CxList.module.css';
import { clsx } from 'clsx';

export const CxList = (props: ListProps) => {
  const rbacList = useRbacList();
  const redirect = useRedirect();
  const exporter = useExporter({
    filename: 'CX',
    props: {
      'code': {
        label: 'Credit code',
      },
      'user.fullName': {
        label: 'Full Name',
      },
      'status': {
        label: 'Status',
        prop: 'statusEn',
      },
      'accreditedName': {
        label: 'Accredited',
      },
      'user.phone': {
        label: 'Phone',
        transform: rbac.canShowUserPhone() ? 'phone' : 'masked',
      },
      'signatureAt': {
        label: 'Signature Date',
        transform: 'date',
      },
      'paymentAt': {
        label: 'Payment Date',
        transform: 'date',
      },
      'bankApproval': {
        label: 'Credit Amount',
        transform: 'money',
      },
      'balance': {
        label: 'Balance',
        transform: 'money',
      },
      'cx.hasFarms': {
        label: 'Farms',
        transform: 'done',
      },
      'cx.isSurveyCompleted': {
        label: 'Satisfaction Survey',
        transform: 'done',
      },
      'cx.hasWelcomeCall': {
        label: 'Welcome call',
        transform: 'done',
      },
      'cx.hasFollowUp': {
        label: 'Follow Up',
        transform: 'done',
      },
      'cx.hasCoveredCrop': {
        label: 'Covered Crop',
        transform: (hasCoveredCrop: boolean) => hasCoveredCrop ? 'Done' : 'Pending',
      },
      'cx.lastLog': {
        label: 'Last Log',
      },
      'mainBuyerName': {
        label: 'Withholding Company',
      },
      'retention': {
        label: 'Retention',
        transform: 'money',
      },
      'ptFinalBalance': {
        label: 'P-T Final Balance',
        transform: 'money',
      },
      'pfFinalBalance': {
        label: 'P-F Final Balance',
        transform: 'money',
      },
    },
  });

  const mappingFuncWelcomeCall = (item: CxCredit) => {
    const itemExport = {
      'Credit Code': item.code,
      'Accredited': item.accredited ?? '',
      'Credit Status': item.statusEn ?? '',
    };
    for (const question of item.cx.questionsWelcomeCall) {
      itemExport[question.name] = question.answer || '';
    }
    return itemExport;
  };

  const mappingFuncFollowUp = (item: CxCredit) => {
    const itemExport = {
      'Credit Code': item.code,
      'Accredited': item.accreditedName ?? '',
      'Credit Status': item.statusEn ?? '',
    };
    for (const question of item.cx.questionsFollowUp) {
      itemExport[question.name] = question.answer || '';
    }
    return itemExport;
  };

  if (!rbacList) {
    return <Forbidden />;
  }

  const onExport = async (queryParams: any) => {
    const positions = await cxHttp.export(queryParams);
    exportToCsv(positions, 'CX Satisfaction Survey', mappingFuncWelcomeCall);
    setTimeout(() => exportToCsv(positions, 'CX Follow Up', mappingFuncFollowUp), 100);
  };

  return (
    <GridList {...props}
      filters={Filters}
      sort={{ field: 'id', order: 'DESC' }}
      hideRemoveButton
      exporter={exporter}
      onExport={onExport}
    >
      <Grid
        rowClick="edit"
        defaultColumns={['code', 'status', 'accreditedName', 'user.phone', 'balance',
          'cx.hasFarms', 'cx.hasWelcomeCall', 'cx.hasFollowUp',
          'cx.hasCoveredCrop', 'cx.hasLastLog',
          'mainBuyerName', 'retention', 'ptFinalBalance', 'pfFinalBalance',
        ]}
      >
        <TextField source="code" label="Credit Code" />
        <FunctionField source="user.fullName" label="Full name" render={(record: CxCredit) => {
          return <span
            className={cls.link}
            onClick={e => {
              e.preventDefault();
              e.stopPropagation();
              redirect(`/user/${record.user.id}?initial`);
            }}
          >{record.user.fullName}</span>;
        }} />
        <CreditStatusField source="status" label="Status" />
        <TextField source="accreditedName" label="Accredited" />
        <PhoneField source="user.phone" label="Phone" isMasked={!rbac.canShowUserPhone()} />
        <DateField source="signatureAt" label="Signature Date" />
        <DateField source="paymentAt" label="Payment Date" />
        <MoneyField source="bankApproval" label="Credit Amount"
          style={{ display: 'inline-block', width: 100 }} />
        <MoneyField source="balance" label="Balance"
          style={{ display: 'inline-block', width: 100 }} />
        <DonePendingField source="cx.hasFarms" label="Farms" />
        <DonePendingField source="cx.isSurveyCompleted" label="Satisfaction Survey" />
        <DonePendingField source="cx.hasWelcomeCall" label="Welcome Call" />
        <DonePendingField source="cx.hasFollowUp" label="Follow Up" />
        <CheckboxField source="cx.hasCoveredCrop" label="Covered Crop" />
        <FunctionField source="cx.lastLog" label="Last Log" render={(record: CxCredit) =>
          <span className={clsx(
            cls.text,
            record.cx.lastLog && record.cx.lastLog.length > 140 && cls.longText)}>
            {record.cx.lastLog ? record.cx.lastLog.trim() : ''}
          </span>
        } />
        <TextField source="mainBuyerName" label="Withholding Company" />
        <MoneyField source="retention" label="Retention"
          style={{ display: 'inline-block', width: 100 }} />
        <MoneyField source="ptFinalBalance" label="P-T Final Balance"
          style={{ display: 'inline-block', width: 100 }} />
        <MoneyField source="pfFinalBalance" label="P-F Final Balance"
          style={{ display: 'inline-block', width: 100 }} />
      </Grid>
    </GridList>
  );
};

const Filters = [
  <TextInput source="q" key="q" label="Search" alwaysOn style={{ width: 200 }} />,
  <SelectArrayInput
    key="status"
    source="status"
    choices={cxStatusOptions}
    className="MS-field"
    style={{ width: 230 }}
    alwaysOn
  />,
  <DateRangeSelect
    key="createdAt"
    source="createdAt"
    label="Signature Date/Payment Date/Satisfaction Survey/Follow Up"
    alwaysOn
    choices={[
      { id: 'signatureAt', name: 'Signature Date' },
      { id: 'paymentAt', name: 'Payment Date' },
      { id: 'welcomeCallFinishedAt', name: 'Satisfaction Survey' },
      { id: 'followUpFinishedAt', name: 'Follow Up' },
    ]}
  />,
  <NullableBooleanInput key="hasFarms" source="hasFarms" label="Farms"
    trueLabel="Done" falseLabel="Pending" />,
  <NullableBooleanInput key="isSurveyCompleted"
    source="isSurveyCompleted"
    label="Satisfaction Survey"
    trueLabel="Done"
    falseLabel="Pending" />,
  <NullableBooleanInput key="hasWelcomeCall" source="hasWelcomeCall" label="Welcome Call"
    trueLabel="Done" falseLabel="Pending" />,
  <NullableBooleanInput key="hasFollowUp" source="hasFollowUp" label="Follow Up"
    trueLabel="Done" falseLabel="Pending" />,
  <NullableBooleanInput key="hasCoveredCrop" source="hasCoveredCrop" label="Covered Crop"
    trueLabel="Done" falseLabel="Pending" />,
];
